<template>
	<div id="app" :class="assignAppBackground()" :key="refreshKey">
		<BaseOffline/>
		<header-adapter v-if="hasNavigation" adapter-name="AppHeader" path="header" id="app-header"/>
		<div id="main-container" :class="styleForContainer()" ref="mainContainer">
			<order-tracker-container v-if="showTrackerInApp()"></order-tracker-container>
			<transition-route />
		</div>
		<mobile-checkout-footer />
		<item-detail-actions-mobile v-if="showItemDetailActionFooter()" />
		<app-footer v-if="routeHasFooter()" ref="appFooter"/>
		<modals-container/>
		<tracking></tracking>
	</div>
</template>

<script>
	import _Adapter from '@Adapter';
	import AppFooter from '@/components/footer/AppFooter';
	import OrderTrackerContainer from '@/components/orders/OrderTrackerContainer';
	import MobileCheckoutFooter from '@Checkout/MobileCheckoutFooter.vue';
	import ItemDetailActions from '@Menu/ItemDetailActions.vue';
	import TransitionRoute from '@/components/TransitionRoute.vue';
	import { APP_STORE } from '@/stores/defs';
	import { LOCATION } from '@/stores/defs';
	import { mapActions, mapGetters } from 'vuex';
	import { includes } from 'lodash';

	import globalRegistry from './globalRegistry';
	import locationService from '@/services/location.service';
	import EventBus from "@EventBus";

	import storeLoaderMixin from '@/components/mixins/storeLoaderMixin';
	import flowStateMixin from '@/components/mixins/flowStateMixin';
	import routeMixin from '@/components/mixins/routeMixin';
	import headerMixin from '@Mixin/headerMixin';
	import layoutMixin from '@Mixin/layoutMixin';
	import RecentOrderModel from './models/RecentOrderModel';
	import guestService from './services/guest.service';
	import loginMixin from './components/mixins/loginMixin';
	import navigationMixin from './components/mixins/navigationMixin';
	import Tracking from './components/tracking/Tracking.vue';

	globalRegistry();

	export default {
		mixins: [
			storeLoaderMixin,
			flowStateMixin,
			routeMixin,
			headerMixin,
			layoutMixin,
			loginMixin,
			navigationMixin
		],
		name: 'App',
		components: {
			'header-adapter': _Adapter,
			'app-footer': AppFooter,
			'transition-route': TransitionRoute,
			'mobile-checkout-footer': MobileCheckoutFooter,
			'item-detail-actions-mobile': ItemDetailActions,
			'order-tracker-container': OrderTrackerContainer,
			'tracking': Tracking
		},
		computed: {
			...mapGetters({
				loaderStatus: APP_STORE.LOADED,
				refreshKey: APP_STORE.GET_REFRESH_KEY,
				location: LOCATION.GET_LOCATION
			}),
			hasNavigation() {
				return this.$route.meta.hasNav !== false;
			},
			isHome() {
				return this.$route.name === 'home';
			},
			isAccountRoute() {
				const routeName = this.$route.name;
				if (!routeName) {
					return false;
				}
				const isAccountRoute = (routeName.indexOf('account') !== -1);
				return isAccountRoute;
			},
			isNotFound() {
				return this.$route.name === 'not-found';
			},
		},
		methods: {
			assignAppBackground() {
				return {
					'grey-background': (this._isCheckout && this.$mq !== 'mobile')
				}
			},
			initialLoad() {
				const appLoaded = this.loaderStatus;
				if (!appLoaded) {
					this.loadAllFromStorage();
					const location = locationService.getLocationFromStorage()
					const locationId = (location && location.id) ? location.id : null;
					this.loadAll(locationId);
				}
			},
			routeHasFooter() {
				const route = this.$route;
				const routeName = route.name;
				const routesWithoutFooter = ['item-detail'];
				const atCheckout = route.meta.isCheckout;

				if (includes(routesWithoutFooter, routeName)) {
					return false;
				}
				if (this._isCheckout) {
					return false;
				}
				return true;
			},
			styleForContainer() {
				const isMinimal = (this.$appConstants.checkout.minimal === '1');
				const desktop = (this.$mq === 'desktop');
				return {
					'container': true,
					'navbar--increased-margin': this.isHome,
					'container-width-limit': this._isCheckout,
					'remove-top': this._isCheckout && desktop,
					'at-checkout': this._isCheckout,
					'minimal': isMinimal,
					'account-view': this.isAccountRoute,
					'container--not-found': this.isNotFound,
					'order-confirmation': this._isOrderConfirmation,
				};
			},
			setupDocumentEventListeners() {
				const body = document.body;
				body.classList.add('using-mouse');
				body.addEventListener('mousedown', () => {
					body.classList.add('using-mouse');
				});
				body.addEventListener('keydown', (event) => {
					const tabkey = 9;
					if(event.keyCode == tabkey) {
						body.classList.remove('using-mouse');
					}
				});
			},
			updateAppHeader() {
				const rootElement = this.$root.$el;
				const headerAdapter = rootElement.querySelector('#app-header');
				if (!headerAdapter) { return; }
				const header = headerAdapter.querySelector('.header');
				if (!header) { return; }
				let headerHeight = header.offsetHeight;
				if (header.classList.contains('sidebar')) {
					const orderTracker = header.querySelector('.order-tracker-container');
					const height = orderTracker && orderTracker.offsetHeight || 0;
					headerHeight = height;
				}
				const mainContainer = rootElement.querySelector('#main-container');
				// TODO make generalized white list
				const routes = [
					'checkout',
					'order-confirmation-mobile',
					'account-sign-in',
					'account-sign-up',
					'account-guest-form',
					'account-enter-pin',
					'account',
					'item-detail'
				];
				if (this.whiteListRoute(routes)) {
					mainContainer.style.top = '0';
					mainContainer.style.paddingTop = '0';
					return;
				}
				const $appConstants = this.$appConstants;
				const mobileHeaderRouteWhiteList = [
					'home',
				];
				const hasPadding = this.whiteListRoute(mobileHeaderRouteWhiteList);
				if (hasPadding && this.$isMobile) {
					mainContainer.style.paddingTop = $appConstants.navigationHeightMobile;
				} else {
					mainContainer.style.paddingTop = '0';
				}
			},
			showTrackerInApp() {
				const orderConfirmation = this._isOrderConfirmation;
				if (orderConfirmation) {
					return false;
				}
				const activeOrder = this._getActiveOrder;
				const startTime = activeOrder && activeOrder.api_order && activeOrder.api_order.startTime;
				const orderDate = startTime && new Date((startTime + 86400) * 1000);
				if (activeOrder instanceof RecentOrderModel) {
					return true;
				}
				if (!startTime || !activeOrder || new Date() > orderDate) {
					return false;
				}
				return true;
			},
			showItemDetailActionFooter() {
				const mobile = this.$mq !== 'desktop';
				const isItemRoute = this._isItemRoute;
				return mobile && isItemRoute;
			},
			getFooter() {
				const footerRef = this.$refs['appFooter'];
				if (!footerRef) {
					return false;
				}
				const footer = this.$refs['appFooter'].$el;
				return footer;
			},
		},
		mounted() {
			this.initialLoad();
			const appId = this.$appConstants.locationBase;
			this.$tpa.initialize(appId);

			this.setupDocumentEventListeners();
		},
		updated() {
			window.requestAnimationFrame(() => {
				this.updateAppHeader();
			});
		},
		created() {
			EventBus.$on("store-changed", this.loadNewStore);
			EventBus.$on('recalculate-header', this.updateAppHeader);
			// Check if we have external auth
			const url = new URL(window.location.href);
			const params = new URLSearchParams(url.search);
			if(params.has('auth')) {
				const auth = params.get('auth');
				const parsedAuth = JSON.parse(atob(auth));
				if(
					'franchisorBase' in parsedAuth &&
					'userBase' in parsedAuth &&
					'apiKey' in parsedAuth
				) {
					this.sendExternalLoginRequest(parsedAuth.franchisorBase, parsedAuth.userBase, parsedAuth.apiKey).then((response) => {
						const data = response.data;
						const message = data.message;
						if (message !== 'Successfully logged in') {
							// show error
							return;
						}
						const body = data.body;
						const customerData = body.customer;
						const raw = customerData && customerData.customer;
						console.log(raw);
						this.setCustomerLoggedIn(raw);
						this._navigateToHome();
						this.$router.go(this.$router.currentRoute);
					});
				}
			}
		},
		destroyed() {
			EventBus.$off('store-changed');
			EventBus.$off('recalculate-header');
		},
		watch: {
			_headerHeight(newValue, oldValue) {
				const footer = this.getFooter();
				const mainContainer = this.$refs['mainContainer'];
				const routeName = this._getRouteName();
				mainContainer.classList.add(routeName);
				let targets = [];
				const screen = this.$mq;
				if (footer && !this.$layouts.hasSideBar(screen)) {
					targets.push(footer);
				}
				targets.push(mainContainer);
				let height = newValue;
				const routes = [
					'greeting',
					'service',
					'locator',
					'order-confirmation-mobile',
					'account-sign-in',
					'account-sign-up',
					'account-guest-form',
					'account-enter-pin',
					'account',
					'item-detail'
				];
				if (this.whiteListRoute(routes)) {
					height = 0;
				}
				height = `${height}px`;
				if (this._isCheckout && this.$mq !== 'desktop') {
					height = 0;
				}
				this.$anime({
					targets: targets,
					duration: 0,
					top: `0px`,
				}, {
					targets: targets,
					duration: 333,
					top: `${height}`,
					easing: 'easeInOutCirc',
				});
			},
			location(newValue, oldValue) {
				// console.log('Location watch fired ', oldValue, ' to ', newValue);
				// On first page load we have a {id: null} object passed it, we want to skip this change
				if(!oldValue || oldValue.id === null) {
					// console.log('null bailout');
					return;
				}

				// On first page load we get the same location a couple of times so ignore if it the id isn't changing
				if(oldValue.id === newValue.id) {
					// console.log('id match bailout');
					return;
				}

				// Else, we are really changing locations and we want to release the guest (if we have one)
				// console.log('Location Changed from ', oldValue.id, ' to ', newValue.id);
				// console.log('Releasing guest with id: ', this.guestId, ' from location: ', oldValue.id);
				guestService.releaseGuestCustomer({
					base: oldValue.id,
					customerId: null
				});
			}
		},
	};
</script>

<style lang="scss">
	@import 'bulma';
	@import 'bulma-tooltip';
	@import 'common-style';
	@import url($load-font);
	html, body {
		margin: 0;
		background-color: $landingPageBackground;
		height: 100%;
		touch-action: manipulation;
	}
	#app {
		font-family: $font-family;
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
	}
	.v--modal-overlay.scrollable .v--modal-box {
		transition: all 0.33s ease;
	}
	.v--modal {
		box-shadow: 0 4px 11px -4px rgba(27, 33, 58, 0.4);
	}
	.container--not-found {
		margin-top: 10vh;
	}
	@media (max-width: #{$mobile-width}) {
		.menu-categories-container:not(.is-desktop) {
			display: block;
		}
	}
</style>



// WEBPACK FOOTER //
// src/App.vue